import type { WeeklyAnalyticsRow } from "../../types";
import { TypeIcon, type useIssuesMeta } from "../issues/meta";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { formatHours, percent, shortWeek } from "./metrics";

export function TypeBreakdownTable({
  rows,
  meta,
  week,
}: {
  rows: WeeklyAnalyticsRow[];
  meta: ReturnType<typeof useIssuesMeta>["data"];
  week: string | undefined;
}) {
  if (!week) return null;
  const thisWeek = rows.filter((row) => shortWeek(row.weekStart) === week);
  if (!thisWeek.length) return null;
  return (
    <Card className="gap-0 pb-0">
      <CardHeader className="border-b pb-4">
        <CardTitle>This week by type</CardTitle>
        <CardDescription>Week starting {week}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-2 py-2 text-right font-medium">Created</th>
              <th className="px-2 py-2 text-right font-medium">Completed</th>
              <th className="px-2 py-2 text-right font-medium">Open</th>
              <th className="px-2 py-2 text-right font-medium">Points</th>
              <th className="px-2 py-2 text-right font-medium">Median cycle</th>
              <th className="px-2 py-2 text-right font-medium">SLA met</th>
              <th className="px-4 py-2 text-right font-medium">Via factory</th>
            </tr>
          </thead>
          <tbody>
            {thisWeek
              .sort((a, b) => b.created + b.completed - (a.created + a.completed))
              .map((row) => (
                <tr key={row.typeKey} className="border-b last:border-0">
                  <td className="flex items-center gap-2 px-4 py-2">
                    <TypeIcon
                      typeKey={row.typeKey}
                      color={meta?.types.find((t) => t.key === row.typeKey)?.color}
                    />
                    {meta?.types.find((t) => t.key === row.typeKey)?.name ?? row.typeKey}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.created}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.completed}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.openAtEnd}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.storyPoints}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatHours(row.medianCycleHours)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{percent(row.slaMet, row.slaTotal)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.inFactory}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
